import PoolMatchCard from './PoolMatchCard'
import AdvancerPicker from './AdvancerPicker'
import useBracketStore from '@/stores/useBracketStore'
import { scrollToPoolMatch } from '@/lib/poolScroll'

/**
 * Renders one pool's round-robin matches as a list of PoolMatchCards plus a
 * predicted-standings sidebar. The pool param is one element of
 * poolStructure.pools.
 */
export default function PoolGrid({ pool }) {
  const teams = useBracketStore(s => s.teams)
  const picks = useBracketStore(s => s.picks)
  const canEditPicks = useBracketStore(s => s.canEditPicks)
  const isViewingOfficial = useBracketStore(s => s.isViewingOfficial)
  const isEditingOfficial = useBracketStore(s => s.isEditingOfficial)
  const handleSelectWinner = useBracketStore(s => s.handleSelectWinner)
  const bracket = useBracketStore(s => s.bracket)

  const teamSize = bracket?.divisionTeamSize || 1
  const teamById = new Map(teams.map(t => [t.id, t]))

  return (
    <section className="rounded-lg border bg-background p-4 space-y-3">
      <header className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Pool {pool.pool_id}</h2>
        <span className="text-xs text-muted-foreground">
          {pool.matches.length} match{pool.matches.length === 1 ? '' : 'es'}
        </span>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {pool.matches.map(match => {
          const teamOne = teamById.get(match.team_one_id)
          const teamTwo = teamById.get(match.team_two_id)
          const pick = picks[match.match_id]
          return (
            <PoolMatchCard
              key={match.match_id}
              match={match}
              teamOne={teamOne}
              teamTwo={teamTwo}
              teamSize={teamSize}
              pick={pick}
              canEdit={canEditPicks}
              isViewingOfficial={isViewingOfficial}
              onSelectWinner={(m, teamId) => handleSelectWinner(m, teamId, scrollToPoolMatch)}
            />
          )
        })}
      </div>

      <PoolStandings pool={pool} teamById={teamById} teamSize={teamSize} />

      {/* Explicit "tap who advances" picker (Phase 2, owner 2026-05-29). The
          admin entering official results sees it right away; players see it once
          they've finished their pool picks, pre-filled from their record. It
          replaces the old reactive tiebreaker prompt — the advancer order IS the
          standings now, so there's no separate tie to resolve. */}
      {canEditPicks && (isEditingOfficial || pool.picksDone) && (
        <AdvancerPicker pool={pool} />
      )}
    </section>
  )
}

function PoolStandings({ pool, teamById, teamSize }) {
  // Rows stay in stable record order (owner 2026-05-29) — the box is a fixed
  // reference and doesn't reshuffle as the user reorders who advances. Which
  // teams advance is flagged from the authoritative advancer set, not row
  // position, so the highlight stays truthful even when they deviate from record.
  const advancingIds = pool.advancingIds || new Set()
  // 4-team pools have everyone advance (Finals has 4 slots), so the "top 4"
  // framing reads wrong. Bigger pools cut to 4.
  const headerLabel = pool.teams.length <= 4
    ? 'Projected standings — all advance to Finals'
    : 'Projected standings — top 4 advance to Finals'
  return (
    <div className="rounded-md border bg-muted/30 overflow-hidden">
      <div className="px-3 py-1.5 border-b bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {headerLabel}
      </div>
      <ol className="text-sm">
        {pool.standings.map((s) => {
          const team = teamById.get(s.bracket_team_id)
          const advancing = advancingIds.has(s.bracket_team_id)
          // Losses are only counted against this team for matches the user
          // has actually picked — so a no-picks board shows 0-0, not 0-3
          // (owner report 2026-05-20). Once every match is picked, losses
          // collapse back to matchesPerTeam − wins as before.
          const losses = Math.max((s.matches_played ?? 0) - s.wins, 0)
          // Only flag a team as "tied" once all picks are in. Pre-picksDone,
          // every team is trivially tied at 0 wins → label everywhere is noise.
          const showTied = pool.picksDone && s.tied_group_size > 1
          return (
            <li
              key={s.bracket_team_id}
              className="grid grid-cols-[1fr_2.5rem_2.5rem] items-center gap-2 px-3 py-1.5"
            >
              {/* Dim the name + record for non-advancing teams, but NOT the
                  "tied" label — parent opacity compounds onto children, which
                  made the orange look inconsistent on dimmed rows. */}
              <span className={'truncate' + (advancing ? '' : ' opacity-60')}>
                {team ? <BracketTeamLine team={team} teamSize={teamSize} /> : '—'}
              </span>
              <span className={'text-xs text-right text-muted-foreground tabular-nums' + (advancing ? '' : ' opacity-60')}>
                {s.wins}-{losses}
              </span>
              <span className="text-xs text-right">
                {showTied ? <span className="text-orange-600">tied</span> : ''}
              </span>
            </li>
          )
        })}
      </ol>
      {!pool.ready && (
        <div className="px-3 py-1.5 text-xs text-muted-foreground border-t">
          Standings update as you pick. Make all {pool.matches.length} picks, then choose who advances.
        </div>
      )}
    </div>
  )
}

function BracketTeamLine({ team, teamSize }) {
  if (!team?.players || team.players.length === 0) return <span className="italic text-muted-foreground">TBD</span>
  if (teamSize === 1) return <span>{team.players[0]?.name}</span>
  return <span>{team.players.map(p => p.name).join(' / ')}</span>
}
